/**
 * Module preview plays — test-only data for the leaderboard's three faces.
 *
 * A module's board is built from plays that a hundred learners have to make, and
 * measuring it therefore has two problems a fixture solves: nobody can play a
 * course a hundred times by hand, and the gate that swaps the results list for a
 * podium is a *count*, which means the interface above it would otherwise be
 * unreviewable until a real class had finished. So the preview hands the board
 * invented plays — through the same `buildModuleLeaderboard` the finished module
 * uses, and rendered by the same screens, which is the point: what the preview
 * shows is what ships, with one array of rows swapped out.
 *
 * ## What the roster is built to show
 *
 * The plays are not random. Six of them are named, and each stands for one answer
 * the board has to get right:
 *
 *  - **Rana finishes fast and well** — she is the fastest of the strong
 *    finishers, and she is *not* the highest scorer. She is the claim the whole
 *    ranking exists for: a fast, good run beats a slow, slightly better one.
 *  - **Youssef takes his time and has the highest score of the six**, and Rana
 *    ranks above him. If the board ever ranked on points alone, those two would
 *    swap and the preview would say so out loud.
 *  - **Amina played one capsule twice**, badly and then well. Only her best
 *    attempt counts, so a board that summed every play would show her higher than
 *    she is — and her row's `plays` count is what makes that visible.
 *  - **Bilal and Chloé stopped part way** (`4 of 6` and `2 of 6`), and they are
 *    listed under the ranked rows with no rank at all, which is the rule that
 *    stops an unfinished run competing with a finished one.
 *  - **Elias played nothing but one capsule, in ten seconds**, which is the
 *    degenerate case the ratio would otherwise crown: without the finishers-only
 *    rule he would top the board on a single capsule.
 *
 * The remaining learners, up to twelve for the results state and a hundred and
 * thirty-seven for the full board, are generated deterministically around those
 * six — the same preview twice draws the same table, because a board that
 * reshuffled on every render would be impossible to look at.
 *
 * Only reachable from a dev build: the module screens read the state off a route
 * param that is checked behind `IS_DEV` (see `#/lib/routes/types`).
 */
import {type MacroMessageDescriptor, msg} from '@lingui/core/macro'

import {
  buildModuleAudience,
  type ModuleCaseFacts,
  type ModuleViewerWork,
} from '#/lib/moduleAudience'
import {buildModuleLeaderboard, type ModulePlay} from '#/lib/moduleLeaderboard'
import {moduleRunProgress, type ModuleRunProgress} from '#/lib/moduleRun'
import {CLASS_FIXTURE_LEARNERS, classFixtureDid} from '#/lib/flashbookFixtures'

/** Which face of the board the preview draws. */
export type ModulePreviewState = 'empty' | 'results' | 'crowd' | 'board'

/**
 * The states a switcher offers, in the order they happen.
 *
 * `empty` is what a module looks like on the day it is published, which is the
 * state most creators will actually see and therefore the one worth being able to
 * look at. `results` is a young module — everybody's numbers, no podium — and
 * `board` is the same module after a hundred learners have finished it.
 *
 * `crowd` is the state in between, and it exists because of the one pair of gates
 * that would otherwise be unreachable: the three names in the lead are earned by
 * **players** (more than ninety) while the ten prize winners are earned by
 * **finishers** (a hundred). With only a dozen rows and a hundred and
 * thirty-seven, a board that names its leaders but not its winners cannot be
 * drawn at all — every board big enough to name the leaders is also big enough to
 * name the winners, so the lower rung was a branch nobody could look at. A crowd
 * of ninety-six, finishing at the usual rate, is that board.
 */
export const MODULE_PREVIEW_STATES: {
  id: ModulePreviewState
  /** A descriptor, so the bar that draws them translates them like any copy. */
  label: MacroMessageDescriptor
}[] = [
  {id: 'empty', label: msg`No one yet`},
  {id: 'results', label: msg`A few finishers`},
  {id: 'crowd', label: msg`A crowd of players`},
  {id: 'board', label: msg`A full board`},
]

/** Whether a route param names a preview state. Anything else is not one. */
export function modulePreviewStateOf(
  value: string | undefined,
): ModulePreviewState | null {
  if (!value) return null
  return MODULE_PREVIEW_STATES.some(state => state.id === value)
    ? (value as ModulePreviewState)
    : null
}

/**
 * The reader the preview can pretend to be.
 *
 * The peer experience is for a *learner*, and a preview browser signed into one
 * school account has no way to be one: the account type is not returned by the
 * AppView yet and a teacher has no play of their own class's course (see
 * `#/lib/moduleAudience` for the whole ladder). So the preview names the role out
 * loud — the same door the invented plays come through, for the same reason.
 *
 * `student` is the class's own learner, `teacher` is the reader who wants the way
 * in and no competition; `off` is the honest reading of the real session, which is
 * what an empty stored value means.
 */
export const MODULE_PREVIEW_ROLES: {
  id: 'off' | 'learner' | 'teacher'
  label: MacroMessageDescriptor
}[] = [
  {id: 'off', label: msg`Whoever I am`},
  {id: 'learner', label: msg`A student`},
  {id: 'teacher', label: msg`A teacher`},
]

/** Where the preview keeps the role it was told to pretend to be. */
const PREVIEW_ROLE_KEY = 'module-preview-role'

export type ModulePreviewRole = 'off' | 'learner' | 'teacher'

/** Whether a value names a role the preview knows. */
export function modulePreviewRoleOf(
  value: string | null | undefined,
): ModulePreviewRole {
  return value === 'learner' || value === 'teacher' ? value : 'off'
}

/**
 * The role the preview is pretending to be, if any.
 *
 * Read from storage rather than from a route param, because the card that most
 * needs it is the one in the *feed*, which has no route of its own — the module's
 * own screen carries the switcher, and every surface reads the answer. Read through
 * `storedPreview`, which is what makes an unreadable storage (a private tab) and a
 * preview mode that is off the same answer: `off`, whoever the reader really is.
 */
export function readModulePreviewRole(): ModulePreviewRole {
  return modulePreviewRoleOf(storedPreview(PREVIEW_ROLE_KEY))
}

/** Remembers the role, for every screen drawn after this one. */
export function writeModulePreviewRole(role: ModulePreviewRole): void {
  leaveReel()
  setStored(PREVIEW_ROLE_KEY, role === 'off' ? null : role)
}

/*
 * ## One preview, one store
 *
 * The preview is a handful of facts — which class to invent, who the reader is
 * pretending to be, whether the class holds a friend, how far the run has got,
 * whether the card is drawing the spec sheet — and every surface that draws a
 * module has to agree on all of them: the card in a feed, the module's own page,
 * the post's header. So they are read and written in one place, and a write is
 * *announced* rather than left in storage for the next render to find.
 *
 * That announcement is what this little store is: a set of listeners and one
 * `write` that every key goes through. Without it, a value read once at mount
 * (which is what the card's *Show all cases* chip did) keeps drawing the answer it
 * started with, because nothing about a `localStorage` write re-renders anything —
 * and a preview whose switch does not visibly switch is the one bug that makes a
 * preview useless.
 *
 * What is *not* here is a snapshot to compare. A version counter was tried, and it
 * is the usual shape (`useSyncExternalStore` wants one); what a subscriber needs
 * from a write, though, is only permission to draw again, and every reader here
 * reads storage during the render that draws it — see
 * `#/components/modules/useModulePreviewCase` for what happened when a memo keyed
 * on that counter stood between the two.
 */
const listeners = new Set<() => void>()

/** Swap one key for a value, or drop it when there is nothing to remember. */
function setStored(key: string, value: string | null): void {
  try {
    if (typeof localStorage === 'undefined') return
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    /* A browser that will not store is a preview that will not remember. */
  }
  for (const listener of listeners) listener()
}

/** Watch the preview. Returns the unsubscribe. */
export function subscribeModulePreview(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * ## The master switch
 *
 * One key above every other: whether the preview is on at all. With it off the app
 * is drawing the reader's real world — the class's own plays, the real session, no
 * invented anything — and the reel is not drawn at all
 * (`#/components/modules/ModuleCaseSwitcher`). Nothing below is *deleted* by it:
 * the stored case waits where it was, so a reviewer can flip to the real class to
 * compare and flip back to the case they were looking at.
 *
 * Every reader in this file goes through it (`storedPreview`), which is the point of
 * putting it here rather than at each call site: a switch that has to be remembered
 * in eight places is a switch that is eventually half-thrown — a card drawing an
 * invented class while the leaderboard draws the real one.
 *
 * Only *off* is written down, and *on* is what the absence of the key means. That
 * is the whole reason for the shape: a reviewer who has never touched the switch
 * should find a dev build's tooling where they left it, and storage that has been
 * cleared should return to it — neither of which is true if the default has to be
 * stored. It is not a *fact* like the ones below: it is not written by a case, it is
 * not cleared by `clearModulePreview`, and it says nothing about which case is being
 * looked at (see `#/screens/Settings` for the switch itself, in the developer
 * options, and `readModulePreviewMode` for every reader).
 */
const PREVIEW_MODE_KEY = 'module-preview-mode'

/**
 * Whether module preview mode is on: the reel, the invented cases, all of it.
 *
 * True is the default and the answer whenever storage cannot say otherwise, because
 * the only caller of this is a dev build that has to ask.
 */
export function readModulePreviewMode(): boolean {
  try {
    if (typeof localStorage === 'undefined') return true
    return localStorage.getItem(PREVIEW_MODE_KEY) !== 'off'
  } catch {
    return true
  }
}

/**
 * Remembers the mode, for every screen drawn after this one.
 *
 * It announces itself like any other write (`setStored`), so the cards already on
 * screen redraw the moment the switch is thrown — the reel disappears, and so does
 * every fact it had set.
 */
export function writeModulePreviewMode(on: boolean): void {
  setStored(PREVIEW_MODE_KEY, on ? null : 'off')
}

/**
 * One fact, as storage holds it — or as though nothing were stored, when preview
 * mode is off.
 *
 * The gate, in one place: every reader below is this call plus a parser, so *real
 * mode* is one branch here rather than a condition each surface has to remember.
 * `null` is the right answer for all of them — no case, no invented class, no role,
 * no friend, no work of the reader's own, no run, no sheet — and every parser
 * already reads `null` as its own kind of nothing. Storage is a browser's to
 * withhold (a private tab), which is the same answer: a preview that threw would
 * take the page with it.
 */
function storedPreview(key: string): string | null {
  try {
    if (typeof localStorage === 'undefined') return null
    if (!readModulePreviewMode()) return null
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

/** Where the preview keeps the board state it was told to draw. */
const PREVIEW_STATE_KEY = 'module-preview-state'

/**
 * The board a preview is drawing, if any.
 *
 * Stored beside the role, and for the same reason: the invented plays are chosen on
 * the module's own screen, which has a route, and the surface that most needs to
 * show them is the card in the **feed**, which does not. A reader who picks *A full
 * board* on the module and walks back to the timeline then sees the same class in
 * the card — which is the only way the card's peer half can be looked at at all,
 * because the AppView does not answer `getCapsulePlays` yet.
 *
 * `null` is *the class's own plays*, and is what an unset value means.
 */
export function readModulePreviewState(): ModulePreviewState | null {
  return modulePreviewStateOf(storedPreview(PREVIEW_STATE_KEY) ?? undefined)
}

/** Remembers the board, for every screen drawn after this one. */
export function writeModulePreviewState(
  state: ModulePreviewState | null,
): void {
  leaveReel()
  setStored(PREVIEW_STATE_KEY, state)
}

/**
 * Where the preview keeps whether the leading player is *a friend*.
 *
 * The one piece of the class block that the invented plays cannot reach on their
 * own. Every other line is a reading of the board — how many played, who is
 * ahead, whether anybody has finished — and the board is exactly what a fixture
 * supplies. *A name the reader recognises* is not: it is a follow, which lives on
 * a profile, and no invented learner has one. So the preview says it out loud,
 * the same way it says which role to pretend to be, and the card copies that
 * line — and only that line — for the leading fixture row.
 *
 * Stored rather than passed, because the surface that needs it is the card in a
 * feed, which has no route and no switcher of its own.
 */
const PREVIEW_FRIEND_KEY = 'module-preview-friend'

/** Whether the preview is drawing a friend in the class. */
export function readModulePreviewFriend(): boolean {
  return storedPreview(PREVIEW_FRIEND_KEY) === 'on'
}

/** Remembers the friend, for every screen drawn after this one. */
export function writeModulePreviewFriend(on: boolean): void {
  leaveReel()
  setStored(PREVIEW_FRIEND_KEY, on ? 'on' : null)
}

/**
 * Where the preview keeps whether the reader works in *this* module's own line.
 *
 * The card's one remaining fact that no device can be talked into. *Play for
 * inspiration* is a claim about the reader's own published work
 * (`moduleIsTheReadersWork`), and a preview browser is signed into the maker's own
 * account — whose modules are the very ones being looked at, so the app can invent
 * a class, a seat, a follow and a run, and it cannot invent a second teacher. The
 * preview says it instead, and what it says is the widest true version of the
 * fact: *the course being looked at is this reader's line*, which is what
 * `modulePreviewWorkOf` reads a module's own level and subject into.
 *
 * Stored rather than passed, like the friend and for the same reason: the surface
 * that needs it is the card in a feed, which has no route and no switcher of its
 * own.
 */
const PREVIEW_WORK_KEY = 'module-preview-work'

/** Whether the preview is drawing a reader who works in this module's line. */
export function readModulePreviewWork(): boolean {
  return storedPreview(PREVIEW_WORK_KEY) === 'on'
}

/** Remembers the reader's line of work, for every screen drawn after this one. */
export function writeModulePreviewWork(on: boolean): void {
  leaveReel()
  setStored(PREVIEW_WORK_KEY, on ? 'on' : null)
}

/**
 * A module's own level and subject, read as the work of somebody who teaches it.
 *
 * The one shape the preview's *works in this line* flag can take, and the same one
 * the reader's real work takes (`#/components/modules/useModuleAudience`), so the
 * two are compared by the same rule: the previewed reader works in exactly what the
 * module being looked at is about, and *play for inspiration* follows. Either field
 * may be missing, and the missing one contributes nothing.
 */
export function modulePreviewWorkOf({
  level,
  subject,
}: {
  level?: string | null
  subject?: string | null
}): ModuleViewerWork {
  const own = (value: string | null | undefined) =>
    typeof value === 'string' && value.trim() ? value.trim() : null
  const ownLevel = own(level)
  const ownSubject = own(subject)
  return {
    levels: ownLevel ? [ownLevel] : [],
    subjects: ownSubject ? [ownSubject] : [],
  }
}

/**
 * Where the preview keeps whether the card should draw **every case at once**.
 *
 * One press, and the card stops telling the story that happens to be true and
 * prints **every case of the reel instead** — one block of copy per case, each row
 * read off a board invented for it (`moduleCaseFacts`), so the whole deck can be
 * read in one screen rather than hunted down one localStorage key at a time.
 *
 * It is a *reader* of the three switches above, and a partner of the reel rather
 * than a fourth fact of its own: asked for, it also requests the biggest board, a
 * student's seat and a fixture friend, because the real card drawn around the deck
 * is read from those (see `useModuleAudience`). Stored rather than passed, because
 * the surface that needs it is the card in a feed, which has no route of its own.
 */
const CASES_KEY = 'module-cases-preview'

/** Whether the card is drawing every case of the class block. */
export function readModuleCasesPreview(): boolean {
  return storedPreview(CASES_KEY) === 'on'
}

/** Remembers the test mode, for every screen drawn after this one. */
export function writeModuleCasesPreview(on: boolean): void {
  leaveReel()
  setStored(CASES_KEY, on ? 'on' : null)
}

/**
 * ## The reel: one press, one whole case
 *
 * A preview that changes one fact at a time is a preview you cannot look at
 * anything with. The card's sentence is a *ladder* — a hundred finishers, then
 * ninety players, then a friend, then the plain count — and reaching the fourth
 * rung means having a board of the right size, a seat of the right kind and a
 * follow that the fixtures cannot invent, all at once. Setting those one key at a
 * time in a console is not reviewing; it is archaeology.
 *
 * So the cases are written down in the order a reader meets them, each one a
 * complete set of answers, and the switcher at the top of the header steps through
 * them. What a case *is* is deliberately boring: four stored facts and a flag,
 * applied through the same writers the bars use, so a case cannot show anything
 * the app cannot otherwise reach.
 */
export type ModulePreviewCase = {
  id: string
  /** What the switcher calls it, in the words of the case rather than its params. */
  label: MacroMessageDescriptor
  /** The class to invent for this case. */
  state: ModulePreviewState
  /** Who the reader is pretending to be. */
  role: ModulePreviewRole
  /** Whether the class's leader counts as somebody the reader follows. */
  friend: boolean
  /**
   * Whether the reader works in this module's own line.
   *
   * The fact the newest word on the button is read from — *play for inspiration*
   * rather than *play module* (`moduleIsTheReadersWork`) — and the only one no
   * device can be talked into: a preview browser is signed into the maker's own
   * account, whose modules are the ones being looked at. See *where the preview
   * keeps the reader's own line of work* below for what the flag says.
   */
  work: boolean
  /** How far the run has got; `null` is whatever this device really has. */
  run: ModulePreviewRun | null
  /** Draw every rung at once — the spec sheet — instead of the true one. */
  cases: boolean
}

/**
 * Every case the card can be asked for, in the order the class lives them.
 *
 * Read top to bottom, the reel is the story of a module: published and unplayed,
 * a few finishers, a crowd, a friend, a name at the top of the class, a full board,
 * a teacher looking at somebody else's work, a teacher whose own modules are in the
 * same subject, a learner part way through the run, a learner one sitting from the
 * end, and finally the spec sheet that shows every rung of the sentence beside the
 * others.
 *
 * The run cases sit on the *small* board on purpose: the sentence about the class
 * and the sentence about the run are two different sentences, and a run case read
 * against a hundred and thirty-seven rows would be reviewed with the wrong half of
 * the card shouting.
 */
export const MODULE_PREVIEW_CASES: ModulePreviewCase[] = [
  {
    id: 'first',
    label: msg`Nobody has played this module yet`,
    state: 'empty',
    role: 'learner',
    friend: false,
    work: false,
    run: null,
    cases: false,
  },
  {
    id: 'finishers',
    label: msg`A few finishers`,
    state: 'results',
    role: 'learner',
    friend: false,
    work: false,
    run: null,
    cases: false,
  },
  {
    id: 'leaders',
    label: msg`Ninety players, three names`,
    state: 'crowd',
    role: 'learner',
    friend: false,
    work: false,
    run: null,
    cases: false,
  },
  {
    id: 'known-face',
    label: msg`A player you follow`,
    state: 'results',
    role: 'learner',
    friend: true,
    work: false,
    run: null,
    cases: false,
  },
  {
    id: 'friend-leading',
    label: msg`A friend among the leaders`,
    state: 'crowd',
    role: 'learner',
    friend: true,
    work: false,
    run: null,
    cases: false,
  },
  {
    id: 'friend-won',
    label: msg`A friend among the prize winners`,
    state: 'board',
    role: 'learner',
    friend: true,
    work: false,
    run: null,
    cases: false,
  },
  {
    id: 'prize',
    label: msg`A full board`,
    state: 'board',
    role: 'learner',
    friend: false,
    work: false,
    run: null,
    cases: false,
  },
  {
    id: 'teacher',
    label: msg`A teacher reading it`,
    state: 'board',
    role: 'teacher',
    friend: false,
    work: false,
    run: null,
    cases: false,
  },
  {
    id: 'inspiration',
    label: msg`A teacher in the same subject`,
    state: 'board',
    role: 'teacher',
    friend: false,
    work: true,
    run: null,
    cases: false,
  },
  {
    id: 'run-one',
    label: msg`One capsule in`,
    state: 'results',
    role: 'learner',
    friend: false,
    work: false,
    run: 'one',
    cases: false,
  },
  {
    id: 'run-three',
    label: msg`Three capsules in`,
    state: 'results',
    role: 'learner',
    friend: false,
    work: false,
    run: 'three',
    cases: false,
  },
  {
    id: 'run-half',
    label: msg`Past halfway`,
    state: 'results',
    role: 'learner',
    friend: false,
    work: false,
    run: 'half',
    cases: false,
  },
  {
    id: 'run-done',
    label: msg`Every capsule done`,
    state: 'results',
    role: 'learner',
    friend: false,
    work: false,
    run: 'all',
    cases: false,
  },
  {
    id: 'sheet',
    label: msg`Every case at once`,
    state: 'board',
    role: 'learner',
    friend: true,
    work: false,
    run: null,
    cases: true,
  },
]

/** Where the preview keeps the case it is showing, when it is showing one. */
const PREVIEW_CASE_KEY = 'module-preview-case'

/** Whether a value names a case in the reel. */
export function modulePreviewCaseOf(
  value: string | null | undefined,
): ModulePreviewCase | null {
  if (!value) return null
  return MODULE_PREVIEW_CASES.find(one => one.id === value) ?? null
}

/** The case being shown, or null when the preview is off the reel. */
export function readModulePreviewCase(): ModulePreviewCase | null {
  return modulePreviewCaseOf(storedPreview(PREVIEW_CASE_KEY))
}

/**
 * Change every fact at once, for a case.
 *
 * Written last, and through the writers that clear it — so the order here is the
 * whole mechanism: the params land, each one leaving the reel, and then the reel
 * is re-entered by its own key.
 */
export function applyModulePreviewCase(id: string): void {
  const found = modulePreviewCaseOf(id)
  if (!found) return
  writeModulePreviewState(found.state)
  writeModulePreviewRole(found.role)
  writeModulePreviewFriend(found.friend)
  writeModulePreviewWork(found.work)
  writeModulePreviewRun(found.run)
  writeModuleCasesPreview(found.cases)
  setStored(PREVIEW_CASE_KEY, found.id)
}

/**
 * Whether a fact was changed by hand, which takes the reader off the reel.
 *
 * The case is a *position* in a list of answers, and a chip that changes one of
 * those answers by hand makes the position a lie — a case labelled *A full board*
 * beside a card drawing an empty one. So every single-fact writer lands here first,
 * and the switcher simply stops claiming a case: what is drawn is what is stored.
 */
function leaveReel(): void {
  setStored(PREVIEW_CASE_KEY, null)
}

/** Forget the whole preview: the reader's own class, seat and run come back.
 *
 * The *mode* is deliberately not one of the things it forgets. A reset means *this
 * case, no case* — the reader's own class while the tool is still in hand — and a
 * press that also switched the preview off would be two features on one button
 * (`writeModulePreviewMode` is the switch).
 */
export function clearModulePreview(): void {
  setStored(PREVIEW_CASE_KEY, null)
  setStored(PREVIEW_STATE_KEY, null)
  setStored(PREVIEW_ROLE_KEY, null)
  setStored(PREVIEW_FRIEND_KEY, null)
  setStored(PREVIEW_WORK_KEY, null)
  setStored(PREVIEW_RUN_KEY, null)
  setStored(CASES_KEY, null)
}

/**
 * ## The run the preview pretends this device is part way through
 *
 * The card says two things about a run — *you have finished three of six* and, when
 * the end is near, 🔥 — and neither can be produced by hand: a run is written by
 * playing a course, and playing six capsules to see the fourth case is not a
 * review. So the preview names the run instead, and the card computes the progress
 * it would have had.
 *
 * It is a *reading*, not a write. The stored runs (`#/state/moduleRuns`) are the
 * learner's own work on this device, and a preview that seeded them would be a
 * preview that had to clean up after itself — and would eventually fail to.
 */
export type ModulePreviewRun = 'one' | 'three' | 'half' | 'all'

/** Where the preview keeps how far the run has got. */
const PREVIEW_RUN_KEY = 'module-preview-run'

/** Whether a value names a run the preview knows. */
export function modulePreviewRunOf(
  value: string | null | undefined,
): ModulePreviewRun | null {
  return value === 'one' ||
    value === 'three' ||
    value === 'half' ||
    value === 'all'
    ? value
    : null
}

/** The run being pretended, if any. */
export function readModulePreviewRun(): ModulePreviewRun | null {
  return modulePreviewRunOf(storedPreview(PREVIEW_RUN_KEY))
}

/** Remembers the run, for every screen drawn after this one. */
export function writeModulePreviewRun(run: ModulePreviewRun | null): void {
  leaveReel()
  setStored(PREVIEW_RUN_KEY, run)
}

/**
 * The capsules a pretended run has finished, as the store records a run.
 *
 * *Past halfway* is `floor(total / 2) + 1` rather than half: the card's rule is
 * about a run that is more than half done (`moduleRunIsClose`), and a case that
 * stopped exactly at the middle would be reviewing the wrong side of the line it
 * exists to show.
 */
export function modulePreviewRunDone(
  items: readonly {uri: string}[],
  run: ModulePreviewRun,
): string[] {
  const total = items.length
  const count =
    run === 'one'
      ? 1
      : run === 'three'
        ? 3
        : run === 'half'
          ? Math.floor(total / 2) + 1
          : total
  return items
    .slice(0, Math.max(0, Math.min(total, count)))
    .map(item => item.uri)
}

/**
 * One invented learner, as the archetypes are written down.
 *
 * `accuracy` is the share of the module's points they score, `pace` multiplies
 * the time a capsule takes them (1 = unhurried), and `finishes` says whether they
 * reached the end — the single fact that decides whether they are ranked at all.
 */
type PreviewLearner = {
  tag: string
  name: string
  accuracy: number
  pace: number
  finishes: boolean
}

/** How long one capsule takes at an unhurried pace, in ms. */
const BASE_CAPSULE_MS = 150_000

/**
 * The named six, in the order they are worth reading.
 *
 * The numbers are chosen so that the *ranking is surprising on purpose*: Rana
 * below Youssef on points, above him on the board, and Elias — who did the least
 * of anybody — nowhere near it.
 */
const MODULE_PREVIEW_LEARNERS: PreviewLearner[] = [
  {tag: 'rana', name: 'Rana Cherif', accuracy: 0.8, pace: 0.55, finishes: true},
  {
    tag: 'youssef',
    name: 'Youssef Amrani',
    accuracy: 1,
    pace: 1.6,
    finishes: true,
  },
  {
    tag: 'amina',
    name: 'Amina Belhaj',
    accuracy: 0.85,
    pace: 0.9,
    finishes: true,
  },
  {
    tag: 'bilal',
    name: 'Bilal Karim',
    accuracy: 0.7,
    pace: 1.1,
    finishes: false,
  },
  {
    tag: 'chloe',
    name: 'Chloé Marchand',
    accuracy: 0.95,
    pace: 0.7,
    finishes: false,
  },
  {tag: 'elias', name: 'Elias Nasri', accuracy: 1, pace: 0.08, finishes: false},
]

/**
 * The last names of the invented learners who fill a full board.
 *
 * A hundred and thirty-seven rows of `learner-84` would make a screenshot
 * worthless — the point of a full board is to *look* like a class — so the extra
 * rows are named from a small vocabulary. They are the six fixture learners'
 * classmates, not real people.
 */
const EXTRA_FIRST_NAMES = [
  'Nour',
  'Karim',
  'Salma',
  'Adam',
  'Lina',
  'Mehdi',
  'Sara',
  'Ilyes',
  'Yasmine',
  'Rayan',
  'Aya',
  'Walid',
  'Hiba',
  'Nassim',
  'Farah',
  'Sami',
  'Dania',
  'Anis',
  'Malak',
  'Ziad',
]
const EXTRA_LAST_NAMES = [
  'Fekih',
  'Bouzid',
  'Mansouri',
  'Trabelsi',
  'Gharbi',
  'Jebali',
  'Khelifi',
  'Sassi',
  'Mejri',
  'Bahri',
]

/**
 * A deterministic pseudo-random number generator.
 *
 * Thirty-two bits of state and one multiply, which is all a fixture needs — and
 * it is *seeded*, so the same capsules give the same board on every render, every
 * reload and every screenshot. `Math.random` would have been one line shorter and
 * would have made the preview unreadable.
 */
function seeded(seed: number): () => number {
  let state = seed >>> 0
  return () => {
    state = (state + 0x6d2b79f5) >>> 0
    let t = state
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** A stable seed from the playlist itself, so a board matches its module. */
function seedOf(capsules: readonly {uri: string}[]): number {
  let seed = 2166136261
  for (const capsule of capsules) {
    for (const char of capsule.uri) {
      seed ^= char.charCodeAt(0)
      seed = Math.imul(seed, 16777619)
    }
  }
  return seed >>> 0
}

/** One learner's rows: their best attempts at each capsule they reached. */
function playsFor({
  learner,
  capsules,
  random,
}: {
  learner: PreviewLearner
  capsules: readonly {uri: string; maxPoints?: number}[]
  random: () => number
}): ModulePlay[] {
  const did = classFixtureDid(learner.tag)
  const handle = `${learner.tag}.test`
  /*
   * How far they got.
   *
   * A finisher plays every capsule; anyone else stops somewhere in the back half
   * of the playlist, which is where people actually stop — a fixture that left
   * the unfinished at capsule two would put them all at the bottom of the list and
   * hide the ordering the unfinished rows are sorted by.
   */
  const reached = learner.finishes
    ? capsules.length
    : Math.max(1, capsules.length - 1 - Math.floor(random() * 3))

  const plays: ModulePlay[] = []
  capsules.slice(0, reached).forEach((capsule, index) => {
    const max = capsule.maxPoints ?? 10
    // A wobble per capsule, so a learner's row is not a straight line — a board
    // where everybody scored the same fraction of every capsule looks generated
    // because it is.
    const wobble = 0.85 + random() * 0.3
    const share = Math.max(0, Math.min(1, learner.accuracy * wobble))
    const score = Math.round(max * share)
    const duration = Math.round(
      BASE_CAPSULE_MS * learner.pace * (0.85 + random() * 0.3),
    )
    plays.push({
      capsuleUri: capsule.uri,
      learnerDid: did,
      learnerHandle: handle,
      learnerName: learner.name,
      score,
      timeMs: duration,
      at: new Date(Date.UTC(2026, 8, 20 + index)).toISOString(),
    })
  })

  /*
   * Amina's first attempt at the module's *second* capsule, and a bad one.
   *
   * It is prepended, so the rows read in the order they happened, and it is there
   * to make the best-attempt rule visible: her points come from the later, better
   * run. A board that summed every play would put her above Rana and the preview
   * would show it.
   */
  if (learner.tag === 'amina' && capsules.length > 1) {
    const capsule = capsules[1]
    plays.unshift({
      capsuleUri: capsule.uri,
      learnerDid: did,
      learnerHandle: handle,
      learnerName: learner.name,
      score: 0,
      timeMs: 40_000,
      at: new Date(Date.UTC(2026, 8, 19, 9)).toISOString(),
    })
  }

  return plays
}

/**
 * The plays a preview shows for a module.
 *
 * `capsules` is the playlist in order, each with the points it is worth when the
 * caller knows them — the numbers a learner scores are built from those maxima,
 * so a module of three-point capsules previews as a three-point module rather
 * than as a plausible-looking lie.
 */
export function modulePreviewPlays(
  capsules: readonly {uri: string; maxPoints?: number}[],
  state: ModulePreviewState,
): ModulePlay[] {
  if (state === 'empty' || capsules.length === 0) return []

  const random = seeded(seedOf(capsules))
  const learners: PreviewLearner[] = [...MODULE_PREVIEW_LEARNERS]

  /*
   * The rest of the class.
   *
   * The results state stops at twelve — a module that a dozen people have played,
   * which is what "a few finishers" means — and the full board goes past the gate
   * so the podium appears. Their accuracies are spread wide and their paces with
   * them, because the whole point of the ratio is that the two orders differ.
   */
  const target = state === 'results' ? 12 : state === 'crowd' ? 96 : 137
  for (let index = learners.length; index < target; index++) {
    const first = EXTRA_FIRST_NAMES[index % EXTRA_FIRST_NAMES.length]
    const last =
      EXTRA_LAST_NAMES[
        (index * 7 + Math.floor(index / EXTRA_FIRST_NAMES.length)) %
          EXTRA_LAST_NAMES.length
      ]
    learners.push({
      tag: `learner-${index}`,
      name: `${first} ${last}`,
      accuracy: 0.4 + random() * 0.6,
      pace: 0.6 + random() * 1.2,
      // Most of a class finishes a course that is set for it; the tail does not.
      finishes: random() > 0.18,
    })
  }

  return learners.flatMap(learner => playsFor({learner, capsules, random}))
}

/**
 * ## The spec deck's own boards
 *
 * The card's *Show all cases* chip stops telling the one case that is true and
 * prints **every case of the reel** instead, one block of copy each, in the order a
 * reader meets them — and each of those blocks has to be the *real* copy of its
 * case. The first build read them all off the one board the reader happened to
 * have, which meant a row could only be filled by the case that board was already
 * in: the other six printed the gate they were waiting for (*needs a board of a
 * hundred finishers*) instead of the sentence they existed to show, and the sheet
 * said nothing at all about the cases a maker cannot otherwise reach.
 *
 * So every case invents its own board, here, from the same table the reel walks
 * (`MODULE_PREVIEW_CASES`): the plays of its state, through the same
 * `buildModuleLeaderboard` and the same `buildModuleAudience` every screen uses —
 * read by the role the case names, and following the leading player when the case
 * says the class holds a friend. Nothing here decides wording; it decides *facts*,
 * and the card's own functions turn those into the sentence, the run line, the
 * reader's own row and the word on the button.
 *
 * Three details are load-bearing:
 *
 *  - **Six capsules worth ten each.** The same stand-in a previewed module scores
 *    against (`#/components/modules/useModuleBoard`), so the run cases land on the
 *    copy they were written for — *1 of 6 capsules done*, *3 capsules left* — rather
 *    than on whatever the module being looked at happens to be. A deck is read for
 *    its wording, and wording that changed with the module could not be proofread.
 *  - **The run cases invent a player.** *You have played 3 of 6 capsules* is the
 *    card's second half, and a case that drew no result of its own would leave the
 *    reader's row out of the sheet. The invented reader finishes exactly the
 *    capsules the case's run says are done, so their row and the run line agree —
 *    and the one case whose run is complete is the one that earns a rank.
 *  - **The boards are remembered.** A hundred and thirty-seven invented learners is
 *    real work, the sheet redraws on every render while it is open, and the same
 *    case must produce the same board every time — a preview that reshuffled would
 *    be impossible to look at, which is the whole reason the plays are seeded.
 */

/**
 * One case of the reel, as the spec sheet prints it.
 *
 * `facts` is everything the block's copy is read from — the sentence, the
 * countdown, the run line, the reader's own row, the word on the button — and
 * `progress` is the run that line is about, absent for the cases that are not
 * about a run at all.
 */
export type ModuleCaseSpec = {
  facts: ModuleCaseFacts
  progress: ModuleRunProgress | null
}

/** Six capsules worth ten each: the module the deck's copy is written against. */
const CASE_CAPSULES = Array.from({length: 6}, (_, index) => ({
  uri: `at://did:plc:previewdeck/app.bsky.feed.capsule/deck-${index + 1}`,
  maxPoints: 10,
}))

/**
 * The subject the deck's invented module is set in.
 *
 * The sheet prints the word on every case's button, and one of those words — *play
 * for inspiration* — is a claim about the *reader's* side of the course rather than
 * about the module, so the module it is read against has to have a side of its own
 * to match: the deck's is `Maths`, and its colleague case is a teacher whose own
 * modules are too. Passed to the build exactly as a screen passes the record's own
 * subject, so the sheet's row is read by the same rule as the card's button.
 */
const CASE_MODULE_SUBJECT = 'Maths'

/** The reader the sheet invents, when a case is about somebody part way through. */
const CASE_READER: PreviewLearner = {
  tag: 'you',
  name: 'You',
  accuracy: 0.9,
  pace: 0.9,
  finishes: true,
}

/** The class each board draws, invented once — it is eight hundred-odd plays. */
const caseClasses = new Map<ModulePreviewState, ModulePlay[]>()

/** The class the board of a case is built from. */
function casePlays(state: ModulePreviewState): readonly ModulePlay[] {
  const cached = caseClasses.get(state)
  if (cached) return cached
  const plays = modulePreviewPlays(CASE_CAPSULES, state)
  caseClasses.set(state, plays)
  return plays
}

/**
 * The invented reader's own plays: exactly the capsules the case's run has done.
 *
 * Generated through the same `playsFor` every fixture learner goes through, so the
 * reader's numbers are the same kind of numbers — and filtered to the run's own
 * capsules, so the row the card prints (*you have played 3 of 6*) and the line it
 * prints above it are the same fact. A case with no run gets no reader at all: an
 * unplayed card has no result to show, and inventing one would put a score on the
 * sheet that its case never says.
 */
function caseReaderPlays(done: readonly string[]): ModulePlay[] {
  if (done.length === 0) return []
  const finished = new Set(done)
  const random = seeded(seedOf(CASE_CAPSULES))
  return playsFor({
    learner: CASE_READER,
    capsules: CASE_CAPSULES,
    random,
  }).filter(play => finished.has(play.capsuleUri))
}

/** The facts of each case, invented once and then read by every render. */
const caseSpecs = new Map<string, ModuleCaseSpec>()

/**
 * The facts behind one case of the reel.
 *
 * The case table is the input, so a case cannot drift from what it says it is: its
 * board is its own `state`, its reader is its own `role`, its friend is its own
 * flag, its line of work its own `work`, and its run its own `run`. A role of `off`
 * reads as the stranger it is —
 * the app's honest answer when nothing says who is looking — which is why no case in
 * the reel uses it.
 */
export function moduleCaseFacts(one: ModulePreviewCase): ModuleCaseSpec {
  const cached = caseSpecs.get(one.id)
  if (cached) return cached

  const done = one.run ? modulePreviewRunDone(CASE_CAPSULES, one.run) : []
  const board = buildModuleLeaderboard({
    capsules: CASE_CAPSULES,
    plays: [...casePlays(one.state), ...caseReaderPlays(done)],
  })
  const lead = board.all[0]
  const audience = buildModuleAudience({
    board,
    viewer: {
      did: one.run ? classFixtureDid(CASE_READER.tag) : undefined,
      as: one.role === 'off' ? null : one.role,
      /*
       * The colleague: a reader whose own modules are in the subject the invented
       * one is set in, which is the fact that turns the teacher's way in into *play
       * for inspiration* — and the reason those are two cases and not one.
       */
      work: one.work
        ? modulePreviewWorkOf({subject: CASE_MODULE_SUBJECT})
        : null,
    },
    level: null,
    subject: CASE_MODULE_SUBJECT,
    /*
     * *A friend in the class* is the preview's own stand-in for a follow, and the
     * one it can mean is the player at the top: the sheet has no profile to read a
     * follow off, and the card's live stranger case does the same thing with the
     * same row (`#/components/modules/useModuleAudience`).
     */
    following: one.friend && lead ? new Set([lead.did]) : undefined,
  })

  const spec: ModuleCaseSpec = {
    facts: {
      isPeer: audience.isPeer,
      played: audience.played,
      finishers: audience.finishers,
      leaders: audience.leaders,
      prizeWinners: audience.prizeWinners,
      friend: audience.friend,
      viewer: audience.viewer,
      cta: audience.cta,
    },
    progress: one.run ? moduleRunProgress({items: CASE_CAPSULES, done}) : null,
  }
  caseSpecs.set(one.id, spec)
  return spec
}

/**
 * The class-report fixture's own learners, re-exported for the preview.
 *
 * Reused rather than re-listed so the two previews cannot disagree about who is
 * in the class; a second roster is a second set of names for the same six people.
 */
export {CLASS_FIXTURE_LEARNERS}
